import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Container, Anchor } from 'components/base';
import { CloudinaryPicture, ScriptMarkupFaq, WIDTH_LADDERS } from 'components/rich';

import { Head, Layout } from '../../components/layout';
import PdpIcon from '../../components/product/pdpIcon';
import ProductFaq from '../../components/product/productFaq';
import tracker from '../../lib/tracker';
import useInView from '../../lib/useInView';
import {
  PLACE,
  SHAPE,
  PLACE_OPTIONS,
  SHAPE_OPTIONS,
  RESULT,
  recommendPrintSize,
} from '../../lib/photoPrintSizes';
import SETTINGS from '../../settings';

// Interactive photo print size chooser, the SEO magnet for "photo print sizes" queries.
// Copy follows PBP-186, layout the "Photo Print Sizes — Redesign" frames in design/website.pen.
// See docs/photo-print-sizes.md.

const PRODUCT_URL = {
  'square-prints': '/square-prints',
  'classic-prints': '/classic-prints',
  'engineer-prints': '/engineer-prints',
  'square-engineer-prints': '/square-engineer-prints',
};

const SECTION_ID = {
  CHOOSER: 'find-your-size',
  CHART: 'size-chart',
  COMPARE: '4x6-vs-5x7',
  FAQ: 'faq',
};

const NAV_ITEMS = [
  { id: SECTION_ID.CHOOSER, label: 'Find your size' },
  { id: SECTION_ID.CHART, label: 'Size chart' },
  { id: SECTION_ID.COMPARE, label: '4×6 vs 5×7' },
  { id: SECTION_ID.FAQ, label: 'FAQ' },
];

const NAV_IDS = NAV_ITEMS.map((item) => item.id);

const PLACE_ICON = {
  [PLACE.DESK]: 'lampDesk',
  [PLACE.WALLET]: 'notebook',
  [PLACE.WALL_CLUSTER]: 'layoutGrid',
  [PLACE.WALL_STATEMENT]: 'image',
};

// Drawn outlines for the shape tiles, [width, height] in px.
const SHAPE_OUTLINES = {
  [SHAPE.SQUARE]: [[20, 20]],
  [SHAPE.RECTANGLE]: [[16, 24]],
  [SHAPE.EITHER]: [
    [12, 12],
    [10, 15],
  ],
};

// Result card content per recommendation path (copy deck B3). `imageKey` is a CDN product
// photo (products/<family>/<n>). CTAs with a `product` key link to the product page and
// carry it in the analytics event, the compare CTA scrolls to the 4x6 vs 5x7 block.
const RESULT_PATHS = {
  [RESULT.SQUARE_4X4]: {
    imageKey: 'square-prints/1',
    headline: '4×4 Square Prints',
    body: 'Our most popular square photo print size, perfect for desks, shelves, and gift stacks. Sets of 20 on thick matte paper.',
    specs: { size: '4 × 4 in', set: '20 prints', paper: 'Thick matte' },
    secondary: 'Want a bit more presence? Try 5.5×5.5 Square Prints.',
    primaryCta: { label: 'Order 4×4 square prints', product: 'square-prints' },
    secondaryCta: { label: 'See all square print sizes', product: 'square-prints' },
  },
  [RESULT.TINY_SQUARE]: {
    imageKey: 'tiny-square-prints/1',
    headline: '2.75×2.75 Tiny Square Prints',
    body: 'Small squares just under 3×3 in, made for wallets, gift tags, journals, and scrapbooks.',
    specs: { size: '2.75 × 2.75 in', set: '20 prints', paper: 'Thick matte' },
    primaryCta: { label: 'Order tiny square prints', product: 'square-prints' },
    secondaryCta: { label: 'See all square print sizes', product: 'square-prints' },
  },
  [RESULT.CLASSIC_4X6]: {
    imageKey: 'classic-prints/1',
    headline: '4×6 Classic Prints',
    body: 'The everyday standard photo print size, easy to hold, gift, and slip into albums.',
    specs: { size: '4 × 6 in', set: '20 prints', paper: 'Thick matte' },
    secondary: 'Prefer a little more room in a frame? See 5×7 below.',
    primaryCta: { label: 'Order 4×6 photo prints', product: 'classic-prints' },
    secondaryCta: { label: 'Compare 4×6 vs 5×7', compare: true },
  },
  [RESULT.CLASSIC_5X7]: {
    imageKey: 'classic-prints/3',
    headline: '5×7 Classic Prints',
    body: 'A standard photo print size with more presence than 4×6, great for tabletops and simple frames.',
    specs: { size: '5 × 7 in', set: '20 prints', paper: 'Thick matte' },
    primaryCta: { label: 'Order 5×7 photo prints', product: 'classic-prints' },
    secondaryCta: { label: 'Compare 4×6 vs 5×7', compare: true },
  },
  [RESULT.SQUARE_WALL]: {
    imageKey: 'square-prints/3',
    headline: '4×4 or 5.5×5.5 Square Prints',
    body: 'Build a photo wall with a set of square prints. 4×4 is the classic grid. 5.5×5.5 reads bigger from a few feet away.',
    specs: { size: '4 × 4 or 5.5 × 5.5 in', set: '20 prints', paper: 'Thick matte' },
    primaryCta: { label: 'Order square prints for your wall', product: 'square-prints' },
    secondaryCta: { label: 'See all square print sizes', product: 'square-prints' },
  },
  [RESULT.CLASSIC_5X7_CLUSTER]: {
    imageKey: 'classic-prints/3',
    headline: '5×7 Classic Prints',
    body: 'Standard rectangular prints that cluster well on a wall or ledge.',
    specs: { size: '5 × 7 in', set: '20 prints', paper: 'Thick matte' },
    primaryCta: { label: 'Order 5×7 classic prints', product: 'classic-prints' },
    secondaryCta: { label: 'Compare 4×6 vs 5×7', compare: true },
  },
  [RESULT.SQUARE_ENGINEER]: {
    imageKey: 'square-engineer-prints/2',
    headline: '3×3 ft Square Engineer Print',
    body: 'One photo, big and bold: 36×36 in on light engineer paper. Best with a high-resolution original (not a heavy Instagram crop).',
    specs: { size: '36 × 36 in', set: '1 print', paper: 'Light 20 lb' },
    primaryCta: { label: 'Order a square engineer print', product: 'square-engineer-prints' },
    secondaryCta: { label: 'Prefer 3×4 ft? See Engineer Prints', product: 'engineer-prints' },
  },
  [RESULT.ENGINEER]: {
    imageKey: 'engineer-prints/3',
    headline: '3×4 ft Engineer Print',
    body: 'A large photo print at 36×48 in, affordable wall-scale art on extra-light paper. Use the biggest original file you have.',
    specs: { size: '36 × 48 in', set: '1 print', paper: 'Light 20 lb' },
    primaryCta: { label: 'Order a large engineer print', product: 'engineer-prints' },
    secondaryCta: {
      label: 'Prefer 3×3 ft? See Square Engineer Prints',
      product: 'square-engineer-prints',
    },
  },
};

const SPEC_LABELS = { size: 'Size', set: 'Set', paper: 'Paper' };

// Size ladder, drawn to scale via the --pps-inch custom property.
const SIZE_LADDER = [
  { label: '5×7', width: 5, height: 7, imageKey: 'classic-prints/3' },
  { label: '4×6', width: 4, height: 6, imageKey: 'classic-prints/1' },
  { label: '5.5×5.5', width: 5.5, height: 5.5, imageKey: 'square-prints/3' },
  { label: '4×4', width: 4, height: 4, imageKey: 'square-prints/1' },
  { label: '2.75×2.75', width: 2.75, height: 2.75, imageKey: 'tiny-square-prints/1' },
];

const COMPARE_CARDS = [
  {
    tag: 'The everyday standard',
    title: '4×6 Classic',
    size: '4 × 6 in',
    width: 4,
    height: 6,
    imageKey: 'classic-prints/1',
    points: ['Fits standard frames and sleeves', 'Best for stacks, mail, albums'],
    cta: { label: 'Order 4×6 classic prints', product: 'classic-prints' },
  },
  {
    tag: 'More presence',
    title: '5×7 Classic',
    size: '5 × 7 in',
    width: 5,
    height: 7,
    imageKey: 'classic-prints/3',
    points: ['Room to breathe with a border', 'Best for tabletops, single frames'],
    cta: { label: 'Order 5×7 classic prints', product: 'classic-prints' },
  },
];

// Copy deck A1, one row per real Parabo size. Glossy shares the Classic sizes and is
// deliberately not listed. `thumb` is the drawn shape in px, relative within the small sizes,
// the two Engineer sizes are shown as tinted "big" shapes.
const SIZE_CHART = [
  {
    size: '2.75 × 2.75 in',
    shape: 'Square',
    bestFor: 'Wallets, gift tags, journals, scrapbooks',
    product: 'Tiny Square Prints · 20 / set',
    thumb: [14, 14],
    cta: { label: 'Order tiny square prints', product: 'square-prints' },
  },
  {
    size: '4 × 4 in',
    shape: 'Square',
    bestFor: 'Desks, shelves, gift stacks, photo walls',
    product: 'Square Prints · 20 / set',
    thumb: [20, 20],
    popular: true,
    cta: { label: 'Order 4×4 square prints', product: 'square-prints' },
  },
  {
    size: '5.5 × 5.5 in',
    shape: 'Square',
    bestFor: 'Bigger squares for walls and frames',
    product: 'Square Prints · 20 / set',
    thumb: [27, 27],
    cta: { label: 'Order 5.5×5.5 square prints', product: 'square-prints' },
  },
  {
    size: '4 × 6 in',
    shape: 'Rectangle',
    bestFor: 'Albums, frames, everyday sharing',
    product: 'Classic Prints · 20 / set',
    thumb: [20, 30],
    cta: { label: 'Order 4×6 classic prints', product: 'classic-prints' },
  },
  {
    size: '5 × 7 in',
    shape: 'Rectangle',
    bestFor: 'Tabletops, simple frames, clusters',
    product: 'Classic Prints · 20 / set',
    thumb: [25, 35],
    cta: { label: 'Order 5×7 classic prints', product: 'classic-prints' },
  },
  {
    size: '36 × 36 in (3 × 3 ft)',
    shape: 'Square',
    bestFor: 'One big square wall piece',
    product: 'Square Engineer Print · 1 print',
    thumb: [36, 36],
    large: true,
    cta: { label: 'Order a square engineer print', product: 'square-engineer-prints' },
  },
  {
    size: '36 × 48 in (3 × 4 ft)',
    shape: 'Rectangle',
    bestFor: 'Affordable wall-scale statement',
    product: 'Engineer Print · 1 print',
    thumb: [30, 40],
    large: true,
    cta: { label: 'Order a large engineer print', product: 'engineer-prints' },
  },
];

const CHART_COLUMNS = ['Size', 'Shape', 'Best for', 'Parabo product', 'Order'];

// FAQPage JSON-LD source. Keep answers in sync with the rendered accordion.
const FAQ = [
  {
    question: 'What are standard photo print sizes?',
    answer:
      'Common standard sizes include 4×6 and 5×7 rectangles, plus square sizes like 4×4. Parabo also makes tiny 2.75×2.75 squares and large engineer prints at 3×4 ft and 3×3 ft.',
  },
  {
    question: 'What size should I print my photos?',
    answer:
      'Match size to where the photo will live: tiny or 4×4 for desks and gifts, 4×6 or 5×7 for classic prints and frames, and engineer sizes for one big wall piece. Use the guide above to get a recommendation.',
  },
  {
    question: 'Is 4×4 the same as a 4 inch square print?',
    answer: 'Yes. Parabo’s 4 inch Square Print is 4×4 inches.',
  },
  {
    question: 'Do you have small square prints around 3×3?',
    answer:
      'Our Tiny Square Print is 2.75×2.75 in, just under 3×3, ideal for wallets, tags, and scrapbooks.',
  },
  {
    question: 'What size should I print Instagram photos?',
    answer:
      'Square prints are the natural fit. A 4×4 Square Print suits a single post, and 5.5×5.5 works well on a photo wall. Instagram saves photos at about 1080 pixels wide, which is enough for any Square or Classic print. Portrait posts get cropped for a square print, so print the original from your camera roll when you can. A saved Instagram photo is too small for an Engineer Print.',
    link: { label: '4×4 Square Print', href: '/square-prints' },
  },
  {
    question: 'What resolution do I need?',
    answer:
      'For Square and Classic prints, aim for 150+ PPI. For large Engineer Prints, aim for about 300 PPI and a strong original file (often 5–10 MB).',
  },
  {
    question: 'Can I print from my iPhone?',
    answer:
      'Yes. The easiest path is the Parabo Press app, which ships real photo prints (not AirPrint to a home printer).',
    link: { label: 'Parabo Press app', href: SETTINGS.APP_STORE_URL },
    guideLink: {
      label: 'How to print photos from iPhone',
      href: '/guides/print-photos-from-iphone',
    },
  },
];

// The rendered answers carry the links, the JSON-LD keeps the plain answer text.
const FAQ_ITEMS = FAQ.map(({ question, answer, link, guideLink }) => {
  const withLink = link
    ? answer.replace(link.label, `<a href="${link.href}">${link.label}</a>`)
    : answer;
  const guide = guideLink
    ? ` See our guide: <a href="${guideLink.href}">${guideLink.label}</a>.`
    : '';
  return { caption: question, description: `<p>${withLink}${guide}</p>` };
});

const BAND_TILES = [
  {
    label: 'Square prints',
    sizes: '2.75, 4 and 5.5 in squares',
    product: 'square-prints',
    imageKey: 'square-prints/3',
  },
  {
    label: 'Classic prints',
    sizes: '4×6 and 5×7',
    product: 'classic-prints',
    imageKey: 'classic-prints/1',
  },
  {
    label: 'Engineer prints',
    sizes: '3×3 ft and 3×4 ft',
    product: 'engineer-prints',
    imageKey: 'engineer-prints/3',
  },
];

const trackCtaClick = (cta, location) => () => {
  tracker.onCustomEvent('photo_print_sizes_cta_click', { cta, location });
};

const optionLabel = (options, id) => options.find((option) => option.id === id)?.label;

const scrollToChooser = () => {
  const element = document.getElementById(SECTION_ID.CHOOSER);
  if (!element) return;
  const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  element.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
};

// Highlights the in-page nav item whose section crosses a band in the upper half of the
// viewport. Nothing is highlighted before any section has been observed.
const useActiveSection = (ids) => {
  const [active, setActive] = useState(null);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: '-30% 0px -60% 0px' },
    );

    ids.forEach((id) => {
      const element = document.getElementById(id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, [ids]);

  return active;
};

const ctaHref = (cta) => (cta.compare ? `#${SECTION_ID.COMPARE}` : PRODUCT_URL[cta.product]);

const ProductCta = ({ cta, location, className, arrow = true, ctaRef }) => (
  <Anchor
    href={ctaHref(cta)}
    className={className}
    onClick={trackCtaClick(cta.compare ? 'compare' : cta.product, location)}
    ref={ctaRef}
  >
    {cta.label}
    {arrow && <PdpIcon name="arrowRight" size={16} />}
  </Anchor>
);

ProductCta.propTypes = {
  cta: PropTypes.shape({
    label: PropTypes.string.isRequired,
    product: PropTypes.oneOf(Object.keys(PRODUCT_URL)),
    compare: PropTypes.bool,
  }).isRequired,
  location: PropTypes.string.isRequired,
  className: PropTypes.string,
  arrow: PropTypes.bool,
  ctaRef: PropTypes.shape({ current: PropTypes.any }),
};

const QuestionHead = ({ number, label }) => (
  <legend className="pps-q-head">
    <span className="pps-q-no" aria-hidden="true">
      {number}
    </span>
    {label}
  </legend>
);

QuestionHead.propTypes = {
  number: PropTypes.number.isRequired,
  label: PropTypes.string.isRequired,
};

const ShapeOutline = ({ shape }) => (
  <span className="pps-shape-outline" aria-hidden="true">
    {SHAPE_OUTLINES[shape].map(([width, height]) => (
      <span key={`${width}x${height}`} style={{ width, height }} />
    ))}
  </span>
);

ShapeOutline.propTypes = {
  shape: PropTypes.oneOf(Object.values(SHAPE)).isRequired,
};

const OptionGroup = ({ name, options, value, onChange, renderVisual, className }) => (
  <div className={classNames('pps-options', className)}>
    {options.map((option) => (
      <label
        key={option.id}
        className={classNames('pps-option', { selected: value === option.id })}
      >
        <input
          type="radio"
          name={name}
          value={option.id}
          checked={value === option.id}
          onChange={() => onChange(option.id)}
        />
        <span className="pps-option-visual">{renderVisual(option.id)}</span>
        <span className="pps-option-label">{option.label}</span>
        <PdpIcon name="circleCheck" size={18} className="pps-option-check" />
      </label>
    ))}
  </div>
);

OptionGroup.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({ id: PropTypes.string.isRequired, label: PropTypes.string.isRequired }),
  ).isRequired,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  renderVisual: PropTypes.func.isRequired,
  className: PropTypes.string,
};

// Photo upload and file checking are deferred to PBP-190.
const Chooser = ({ answers, onAnswer }) => (
  <form
    id={SECTION_ID.CHOOSER}
    className="pps-chooser"
    onSubmit={(e) => e.preventDefault()}
    noValidate
  >
    <fieldset className="pps-q">
      <QuestionHead number={1} label="Where will this photo live?" />
      <OptionGroup
        name="place"
        options={PLACE_OPTIONS}
        value={answers.place}
        onChange={(id) => onAnswer('place', id)}
        renderVisual={(id) => <PdpIcon name={PLACE_ICON[id]} size={22} />}
        className="pps-options-place"
      />
    </fieldset>

    <fieldset className="pps-q">
      <QuestionHead number={2} label="What shape do you want?" />
      <OptionGroup
        name="shape"
        options={SHAPE_OPTIONS}
        value={answers.shape}
        onChange={(id) => onAnswer('shape', id)}
        renderVisual={(id) => <ShapeOutline shape={id} />}
        className="pps-options-shape"
      />
    </fieldset>
  </form>
);

Chooser.propTypes = {
  answers: PropTypes.shape({
    place: PropTypes.string,
    shape: PropTypes.string,
  }).isRequired,
  onAnswer: PropTypes.func.isRequired,
};

const SizeLadder = ({ className, lazy = true }) => (
  <div className={classNames('pps-ladder', className)} aria-hidden="true">
    <div className="pps-ladder-prints">
      {SIZE_LADDER.map((print) => (
        <figure key={print.label} className="pps-ladder-item">
          <span
            className="pps-print"
            style={{
              width: `calc(var(--pps-inch) * ${print.width})`,
              height: `calc(var(--pps-inch) * ${print.height})`,
            }}
          >
            <CloudinaryPicture
              prefix="products/"
              imageKey={print.imageKey}
              widths={WIDTH_LADDERS.products}
              sizes="110px"
              alt=""
              loading={lazy ? 'lazy' : 'eager'}
            />
          </span>
          <figcaption>{print.label}</figcaption>
        </figure>
      ))}
    </div>
    <p className="pps-ladder-scale">
      <span className="pps-ladder-inch" /> = 1 inch. Sizes shown to scale.
    </p>
  </div>
);

SizeLadder.propTypes = {
  className: PropTypes.string,
  lazy: PropTypes.bool,
};

const EmptyPanel = ({ answers }) => {
  const steps = [
    { key: 'place', label: 'Place', value: optionLabel(PLACE_OPTIONS, answers.place) },
    { key: 'shape', label: 'Shape', value: optionLabel(SHAPE_OPTIONS, answers.shape) },
  ];

  return (
    <div className="pps-panel pps-panel-empty">
      <SizeLadder className="pps-ladder-panel" lazy={false} />
      <div className="pps-panel-body">
        <p className="pps-eyebrow">Your result</p>
        <p className="pps-panel-placeholder">Your print size shows up here</p>
        <p className="pps-panel-hint">
          Pick where the photo will live and the shape you want. The result updates as you choose.
        </p>
        <ol className="pps-progress">
          {steps.map((step) => (
            <li key={step.key} className={classNames({ done: Boolean(step.value) })}>
              {`${step.label}: ${step.value ?? 'not chosen yet'}`}
            </li>
          ))}
        </ol>
      </div>
    </div>
  );
};

EmptyPanel.propTypes = {
  answers: PropTypes.shape({ place: PropTypes.string, shape: PropTypes.string }).isRequired,
};

const ResultPanel = ({ answers, path, primaryRef }) => (
  <article className="pps-panel pps-panel-result">
    <div className="pps-panel-photo">
      <CloudinaryPicture
        prefix="products/"
        imageKey={path.imageKey}
        widths={WIDTH_LADDERS.products}
        sizes="(max-width: 991px) 88px, 500px"
        alt=""
      />
      <span className="pps-badge">
        <PdpIcon name="sparkles" size={13} />
        Best match
      </span>
    </div>
    <div className="pps-panel-head">
      <p className="pps-eyebrow">
        <PdpIcon name="sparkles" size={13} className="pps-eyebrow-icon" />
        Your result
        <span className="pps-eyebrow-match"> · Best match</span>
      </p>
      <p className="pps-panel-answers">
        {`${optionLabel(PLACE_OPTIONS, answers.place)} · ${optionLabel(SHAPE_OPTIONS, answers.shape)}`}
      </p>
    </div>
    <div className="pps-panel-title" aria-live="polite">
      <p className="pps-panel-kicker">Here’s the size we’d print</p>
      <h2 className="pps-panel-headline">{path.headline}</h2>
    </div>
    <div className="pps-panel-body">
      <p className="pps-panel-text">{path.body}</p>
      <dl className="pps-specs">
        {Object.entries(path.specs).map(([key, value]) => (
          <div key={key}>
            <dt>{SPEC_LABELS[key]}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {path.secondary && (
        <p className="pps-upsell">
          <PdpIcon name="maximize2" size={14} />
          {path.secondary}
        </p>
      )}
      <div className="pps-panel-actions">
        <ProductCta
          cta={path.primaryCta}
          location="result"
          className="pps-btn pps-btn-lg"
          ctaRef={primaryRef}
        />
        {path.secondaryCta && (
          <ProductCta
            cta={path.secondaryCta}
            location="result"
            className="pps-text-link"
            arrow={false}
          />
        )}
      </div>
    </div>
    <aside className="pps-tip">
      <PdpIcon name="scanSearch" size={18} />
      <div>
        <p className="pps-tip-title">Photo resolution tips</p>
        <p className="pps-tip-text">
          Best results at 150+ PPI for Square and Classic. Large Engineer prints want about 300
          PPI.
        </p>
      </div>
    </aside>
  </article>
);

ResultPanel.propTypes = {
  answers: PropTypes.shape({ place: PropTypes.string, shape: PropTypes.string }).isRequired,
  path: PropTypes.shape({
    imageKey: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    body: PropTypes.string.isRequired,
    specs: PropTypes.object.isRequired,
    secondary: PropTypes.string,
    primaryCta: PropTypes.object.isRequired,
    secondaryCta: PropTypes.object,
  }).isRequired,
  primaryRef: PropTypes.shape({ current: PropTypes.any }),
};

const InPageNav = ({ path }) => {
  const active = useActiveSection(NAV_IDS);

  return (
    <nav className="pps-nav" aria-label="On this page">
      <Container className="pps-nav-inner">
        <ul className="pps-nav-list">
          {NAV_ITEMS.map((item) => (
            <li key={item.id}>
              <Anchor
                href={`#${item.id}`}
                className={classNames('pps-nav-link', { active: active === item.id })}
              >
                {item.label}
              </Anchor>
            </li>
          ))}
        </ul>
        {path && (
          <div className="pps-nav-size">
            <p className="pps-nav-size-label">
              Your size: <strong>{path.headline}</strong>
            </p>
            <ProductCta
              cta={path.primaryCta}
              location="sticky_nav"
              className="pps-btn pps-btn-sm"
              arrow={false}
            />
          </div>
        )}
      </Container>
    </nav>
  );
};

InPageNav.propTypes = {
  path: PropTypes.shape({
    headline: PropTypes.string.isRequired,
    primaryCta: PropTypes.object.isRequired,
  }),
};

// Mobile only (CSS). Shown once answered, while the result's order button is off screen.
// The hidden state uses `visibility`, which also takes the bar out of the tab order.
const OrderBar = ({ path, visible }) => (
  <div className={classNames('pps-order-bar', { visible })}>
    <span className="pps-order-bar-thumb">
      <CloudinaryPicture
        prefix="products/"
        imageKey={path.imageKey}
        widths={WIDTH_LADDERS.products}
        sizes="44px"
        alt=""
      />
    </span>
    <div className="pps-order-bar-text">
      <p className="pps-order-bar-label">
        Your size ·{' '}
        <button type="button" onClick={scrollToChooser}>
          Change
        </button>
      </p>
      <p className="pps-order-bar-value">{path.headline}</p>
    </div>
    <Anchor
      href={PRODUCT_URL[path.primaryCta.product]}
      className="pps-btn"
      onClick={trackCtaClick(path.primaryCta.product, 'sticky_bar')}
    >
      Order prints
      <PdpIcon name="arrowRight" size={16} />
    </Anchor>
  </div>
);

OrderBar.propTypes = {
  path: PropTypes.shape({
    imageKey: PropTypes.string.isRequired,
    headline: PropTypes.string.isRequired,
    primaryCta: PropTypes.object.isRequired,
  }).isRequired,
  visible: PropTypes.bool.isRequired,
};

const SizeChartSection = () => (
  <section id={SECTION_ID.CHART} className="pps-section pps-chart">
    <Container>
      <div className="pps-chart-head">
        <div className="pps-chart-heading">
          <p className="pps-eyebrow">Size chart</p>
          <h2 className="pps-h2">Standard photo print sizes we make</h2>
          <p className="pps-lead">
            Here’s a clear chart of common photo print sizes and which Parabo products match them.
            No mystery SKUs: if it’s on this list, you can order it.
          </p>
        </div>
        <SizeLadder className="pps-ladder-chart" />
      </div>
      <table className="pps-table">
        <thead>
          <tr>
            {CHART_COLUMNS.map((column) => (
              <th key={column} scope="col">
                {column}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {SIZE_CHART.map((row) => (
            <tr key={row.size} className={classNames({ popular: row.popular })}>
              <th scope="row" className="pps-cell-size">
                <span className="pps-cell-size-inner">
                  <span className="pps-thumb" aria-hidden="true">
                    <span
                      className={classNames({ large: row.large })}
                      style={{ width: row.thumb[0], height: row.thumb[1] }}
                    />
                  </span>
                  <span className="pps-size">
                    {row.size}
                    {row.popular && <span className="pps-tag">Most popular</span>}
                  </span>
                </span>
              </th>
              <td className="pps-cell-shape">{row.shape}</td>
              <td className="pps-cell-best">{row.bestFor}</td>
              <td className="pps-cell-product">{row.product}</td>
              <td className="pps-cell-order">
                <Anchor
                  href={PRODUCT_URL[row.cta.product]}
                  className="pps-arrow-link"
                  onClick={trackCtaClick(row.cta.product, 'chart')}
                >
                  {row.cta.label}
                  <PdpIcon name="arrowRight" size={16} />
                </Anchor>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="pps-chart-note">
        Glossy Prints also come in 4×6 and 5×7, the same sizes as Classic, so they are not listed
        separately. Square and Classic print on thick matte FSC paper. Engineer prints use
        extra-light 20 lb paper with a ½ in white border.
      </p>
    </Container>
  </section>
);

const CompareSection = () => (
  <section id={SECTION_ID.COMPARE} className="pps-section pps-compare">
    <Container className="pps-compare-grid">
      <div className="pps-compare-heading">
        <p className="pps-eyebrow">Compare</p>
        <h2 className="pps-h2">4×6 vs 5×7 photo prints</h2>
        <p className="pps-lead">
          Both are classic rectangular photo print sizes. 4×6 is the everyday standard: compact,
          giftable, album-friendly. 5×7 gives you more presence on a shelf or in a simple frame.
          Same thick matte paper, white border or edge-to-edge, sets of 20.
        </p>
      </div>
      <div className="pps-compare-cards">
        {COMPARE_CARDS.map((card) => (
          <article key={card.title} className="pps-compare-card">
            <div className="pps-compare-visual" aria-hidden="true">
              <span
                className="pps-print"
                style={{
                  width: `calc(var(--pps-compare-inch) * ${card.width})`,
                  height: `calc(var(--pps-compare-inch) * ${card.height})`,
                }}
              >
                <CloudinaryPicture
                  prefix="products/"
                  imageKey={card.imageKey}
                  widths={WIDTH_LADDERS.products}
                  sizes="160px"
                  alt=""
                  loading="lazy"
                />
              </span>
              <span className="pps-size-tag">{card.size}</span>
            </div>
            <div className="pps-compare-body">
              <p className="pps-card-tag">{card.tag}</p>
              <h3 className="pps-h3">{card.title}</h3>
              <ul className="pps-points">
                {card.points.map((point) => (
                  <li key={point}>
                    <PdpIcon name="check" size={16} />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
            <ProductCta cta={card.cta} location="compare" className="pps-btn pps-compare-cta" />
          </article>
        ))}
      </div>
    </Container>
  </section>
);

const FaqSection = () => (
  <section id={SECTION_ID.FAQ} className="pdp-section pdp-section-green">
    <Container>
      <ProductFaq
        title="Photo print sizes: FAQ"
        intro="Quick answers on standard sizes, resolution, and printing from your phone."
        items={FAQ_ITEMS}
      />
    </Container>
  </section>
);

const CtaBandSection = () => (
  <section className="pps-section pps-band">
    <Container>
      <div className="pps-band-head">
        <h2 className="pps-h2">Ready to print?</h2>
        <p className="pps-lead">
          Pick your size and order Square, Classic, or Engineer Prints, shipped to your door.
        </p>
      </div>
      <ul className="pps-band-tiles">
        {BAND_TILES.map((tile) => (
          <li key={tile.product} className="pps-band-tile">
            <span className="pps-band-photo">
              <CloudinaryPicture
                prefix="products/"
                imageKey={tile.imageKey}
                widths={WIDTH_LADDERS.products}
                sizes="(max-width: 767px) 64px, 384px"
                alt=""
                loading="lazy"
              />
            </span>
            <span className="pps-band-text">
              {/* The link keeps the plain product anchor text, a pseudo element makes the whole tile clickable. */}
              <Anchor
                href={PRODUCT_URL[tile.product]}
                className="pps-band-link"
                onClick={trackCtaClick(tile.product, 'band')}
              >
                {tile.label}
              </Anchor>
              <span className="pps-band-sizes">{tile.sizes}</span>
            </span>
            <span className="pps-band-arrow" aria-hidden="true">
              <PdpIcon name="arrowRight" size={18} />
            </span>
          </li>
        ))}
      </ul>
    </Container>
  </section>
);

const PhotoPrintSizesPage = () => {
  const [answers, setAnswers] = useState({ place: null, shape: null });
  const primaryRef = useRef();

  const result = recommendPrintSize(answers.place, answers.shape);
  const path = result ? RESULT_PATHS[result] : null;
  const primaryInView = useInView(primaryRef, Boolean(path));

  const handleAnswer = (key, value) => {
    const nextAnswers = { ...answers, [key]: value };
    setAnswers(nextAnswers);

    const nextResult = recommendPrintSize(nextAnswers.place, nextAnswers.shape);
    if (!nextResult) return;
    tracker.onCustomEvent('photo_print_sizes_chooser_complete', {
      place: nextAnswers.place,
      shape: nextAnswers.shape,
      result: nextResult,
      has_photo: false,
    });
  };

  return (
    <>
      <Head
        title="Photo Print Sizes Guide | Find the Right Size"
        description="Free photo print size chart and guide. Compare standard sizes like 4x6 vs 5x7 and find the right size for Square, Classic or large Engineer Prints."
        image={`${SETTINGS.HOST}/seo/products/classic-prints.jpg`}
      />

      <ScriptMarkupFaq items={FAQ} />

      <Layout>
        <div className="pps">
          <section className="pps-hero">
            <Container className="pps-hero-grid">
              <div className="pps-hero-main">
                <div className="pps-hero-copy">
                  <p className="pps-eyebrow">Free photo print sizes guide</p>
                  <h1 className="pps-h1">
                    Photo print sizes: <br />
                    find the right fit
                  </h1>
                  <p className="pps-hero-lead">
                    Answer a few quick questions. We’ll recommend a standard photo print size
                    Parabo actually makes. Then you can order when you’re ready.
                  </p>
                </div>
                <Chooser answers={answers} onAnswer={handleAnswer} />
                <p className="pps-reassure">
                  <PdpIcon name="lockOpen" size={14} />
                  No account or photo upload needed.
                </p>
              </div>
              <div className={classNames('pps-hero-panel', { answered: Boolean(path) })}>
                {path ? (
                  <ResultPanel answers={answers} path={path} primaryRef={primaryRef} />
                ) : (
                  <EmptyPanel answers={answers} />
                )}
              </div>
            </Container>
          </section>

          <InPageNav path={path} />

          <SizeChartSection />
          <CompareSection />
          <FaqSection />
          <CtaBandSection />

          {path && <OrderBar path={path} visible={!primaryInView} />}
        </div>
      </Layout>
    </>
  );
};

export default PhotoPrintSizesPage;
